import React, { useState, useEffect } from 'react';
import { pronunciationAnalyticsService } from '../../services/pronunciationAnalyticsService';
import MetricCard from './components/MetricCard';
import ImprovementChart from './components/ImprovementChart';
import PracticeHeatmap from './components/PracticeHeatmap';
import AIFeedbackAnalysis from './components/AIFeedbackAnalysis';
import StudentRankingsTable from './components/StudentRankingsTable';
import FilterBar from './components/FilterBar';

export default function PronunciationAnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Analytics data
  const [stats, setStats] = useState(null);
  const [improvementTrend, setImprovementTrend] = useState([]);
  const [aiFeedbackPatterns, setAiFeedbackPatterns] = useState(null);
  const [practiceFrequency, setPracticeFrequency] = useState([]);
  const [studentRankings, setStudentRankings] = useState([]);
  const [availablePlays, setAvailablePlays] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    playId: null,
    dateFrom: null,
    dateTo: null,
    cohort: 'all',
    sortBy: 'improvement',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [filters]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      const [
        statsData,
        trendData,
        feedbackData,
        frequencyData,
        rankingsData,
        playsData
      ] = await Promise.all([
        pronunciationAnalyticsService?.getAggregatedStats(filters),
        pronunciationAnalyticsService?.getImprovementTrend(filters),
        pronunciationAnalyticsService?.getAIFeedbackPatterns(filters),
        pronunciationAnalyticsService?.getPracticeFrequency(filters),
        pronunciationAnalyticsService?.getStudentRankings(filters),
        pronunciationAnalyticsService?.getAvailablePlays()
      ]);

      setStats(statsData);
      setImprovementTrend(trendData);
      setAiFeedbackPatterns(feedbackData);
      setPracticeFrequency(frequencyData);
      setStudentRankings(rankingsData);
      setAvailablePlays(playsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load pronunciation analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExportData = () => {
    // Convert data to CSV format
    const csvData = studentRankings?.map(student => ({
      'Student Name': student?.studentName,
      'Total Practice Time': student?.totalPracticeTime,
      'Accuracy Improvement': student?.accuracyImprovement,
      'Streak Maintained': student?.streakMaintained,
      'Challenges Completed': student?.challengesCompleted
    }));

    // Create CSV string
    const headers = Object.keys(csvData?.[0] || {})?.join(',');
    const rows = csvData?.map(row => Object.values(row)?.join(','));
    const csv = [headers, ...rows]?.join('\n');

    // Download CSV file
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pronunciation-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Loading pronunciation analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={loadAnalyticsData}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Pronunciation Analytics Dashboard
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Comprehensive visualization of student pronunciation improvement trends and AI feedback patterns
              </p>
            </div>
            <button
              onClick={handleExportData}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Report
            </button>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <FilterBar
          filters={filters}
          availablePlays={availablePlays}
          onFilterChange={handleFilterChange}
        />
      </div>
      {/* Key Metrics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Practice Sessions"
            value={stats?.totalPracticeSessions || 0}
            icon="📊"
            trend={null}
            color="blue"
          />
          <MetricCard
            title="Average Improvement Score"
            value={`${stats?.averageImprovementScore || 0}%`}
            icon="📈"
            trend={parseFloat(stats?.averageImprovementScore || 0) > 0 ? 'up' : 'down'}
            color="green"
          />
          <MetricCard
            title="Average Accuracy Rate"
            value={`${stats?.averageAccuracyRate || 0}%`}
            icon="🎯"
            trend={null}
            color="purple"
          />
          <MetricCard
            title="Average Practice Streak"
            value={`${stats?.averagePracticeStreak || 0} days`}
            icon="🔥"
            trend={null}
            color="orange"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ImprovementChart data={improvementTrend} />
          <PracticeHeatmap data={practiceFrequency} />
        </div>

        {/* AI Feedback Analysis */}
        <div className="mb-8">
          <AIFeedbackAnalysis data={aiFeedbackPatterns} />
        </div>

        {/* Student Rankings Table */}
        <div>
          <StudentRankingsTable
            data={studentRankings}
            sortBy={filters?.sortBy}
            sortOrder={filters?.sortOrder}
            onSort={(column) => {
              handleFilterChange({
                sortBy: column,
                sortOrder: filters?.sortBy === column && filters?.sortOrder === 'asc' ? 'desc' : 'asc'
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}